import NitroService from "../../../utils/nitro.service";
import {
  FavoritePriceShowAs,
  ICreatePeriodPriceResponse,
  MonthColumn,
  SendPanelCustomRow,
  IFavoriteLocationExtension,
  IFavoritePeriod,
  IFavoritePeriodInfo,
  IFavoriteMonthExtension,
  IFavoriteLocation,
} from "../../../types/IFavoriteOrigins";
import { MONTHS_LOCAL } from "../../../types/IMonth";
import { extendFavorites } from "../../../utils/extendFavorites";

export const toDateTimeParts = (date: Date = new Date()) => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  day: date.getDate(),
  hour: date.getHours(),
  minute: date.getMinutes(),
  second: date.getSeconds(),
  nanosecond: date.getMilliseconds() * 1_000_000,
});

export const getFavoriteSetId = (product: string, marketer: string) =>
  `${marketer}|${product}`;

export const getFavoriteLocId = (
  product: string,
  origin: IFavoriteLocationExtension,
) =>
  origin.id ||
  `${product}|${origin.location.id}|${
    origin.competitor ? "COMPETITOR" : "KOCH"
  }`;

export const buildOrderedFavLocMaps = (
  product: string,
  favorites: IFavoriteLocationExtension[],
  createdIdsByRowKey: Map<string, string>,
  getRowKey: (origin: IFavoriteLocationExtension) => string,
) => {
  const orderedFavorites = [
    ...favorites.filter((favorite) => !favorite.competitor),
    ...favorites.filter((favorite) => favorite.competitor),
  ]
    .map((favorite, index) => ({ favorite, index }))
    .sort((left, right) => {
      const leftOrder = Number.isFinite(left.favorite.order)
        ? Number(left.favorite.order)
        : Infinity;
      const rightOrder = Number.isFinite(right.favorite.order)
        ? Number(right.favorite.order)
        : Infinity;

      if (left.favorite.competitor !== right.favorite.competitor) {
        return (
          Number(left.favorite.competitor) - Number(right.favorite.competitor)
        );
      }
      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return left.index - right.index;
    })
    .map(({ favorite }) => favorite);

  return orderedFavorites
    .map((favorite, order) => {
      const rowKey = getRowKey(favorite);
      const id =
        createdIdsByRowKey.get(rowKey) || getFavoriteLocId(product, favorite);
      if (!id) return null;
      return { id, order };
    })
    .filter(Boolean) as Array<{ id: string; order: number }>;
};

export const refreshFavoritesByMarketer = async (
  marketer: string,
  setFavoriteOrigins: (favorites: any[]) => void,
) => {
  const result = await NitroService.getFavoritesByMarketer({ marketer });
  if (result.length > 0) {
    const extendedFavorites = extendFavorites(result[0].favoriteSets);
    setFavoriteOrigins(extendedFavorites);
    return extendedFavorites;
  }
  return [];
};

export const reorderFavorites = async (
  marketer: string,
  product: string,
  favorites: IFavoriteLocationExtension[],
  getRowKey: (origin: IFavoriteLocationExtension) => string,
  createdIdsByRowKey: Map<string, string> = new Map<string, string>(),
) => {
  const orderedFavLocMaps = buildOrderedFavLocMaps(
    product,
    favorites,
    createdIdsByRowKey,
    getRowKey,
  );

  if (orderedFavLocMaps.length === 0) {
    return;
  }

  await NitroService.reorderFavorites({
    favSetId: getFavoriteSetId(product, marketer),
    orderedFavLocMaps,
  });
};

export const createFavorites = async (
  marketer: string,
  product: string,
  rowsToAdd: IFavoriteLocationExtension[],
  favoritesWithOrder: IFavoriteLocationExtension[],
  getRowKey: (origin: IFavoriteLocationExtension) => string,
) => {
  const createdIdsByRowKey = new Map<string, string>();

  await Promise.all(
    rowsToAdd.map(async (row) => {
      const created = await NitroService.createFavorite({
        marketer,
        product,
        locationId: row.location.id,
        competitor: row.competitor,
        order: Math.max(
          0,
          favoritesWithOrder.findIndex(
            (favorite) => getRowKey(favorite) === getRowKey(row),
          ),
        ),
      });
      const createdId = created[0]?.favLocId;
      if (createdId) {
        createdIdsByRowKey.set(getRowKey(row), createdId);
      }
    }),
  );

  return createdIdsByRowKey;
};

export const deleteFavorite = async (
  marketer: string,
  product: string,
  origin: IFavoriteLocationExtension,
) => {
  await NitroService.deleteFavorite({
    favSetId: getFavoriteSetId(product, marketer),
    favLocId: getFavoriteLocId(product, origin),
  });
};

export const createFavorite = async (
  marketer: string,
  product: string,
  origin: IFavoriteLocation,
  order: number,
) => {
  return NitroService.createFavorite({
    marketer,
    product,
    locationId: origin.location.id,
    competitor: origin.competitor,
    order,
  });
};

export const persistPeriodInfo = async (
  marketer: string,
  updatedOrigin: IFavoriteLocationExtension,
) => {
  const updatedAt = toDateTimeParts();
  let skippedForMissingPeriodId = 0;
  const periodInfoList = updatedOrigin.months.flatMap((monthEntry) =>
    monthEntry.periods.flatMap((period) =>
      (period?.infos || [])
        .filter((row) => row.name.trim() !== "")
        .map((row, index) => {
          let value = "";
          if (row.value !== undefined) {
            value = row.value;
          }

          return {
            periodId: period?.id || "",
            updatedBy: marketer,
            updatedAt,
            name: row.name,
            value,
            order: row.order ?? index + 1,
            valueType: "STRING" as const,
          };
        })
        .filter((row) => {
          if (row.periodId !== "") return true;
          skippedForMissingPeriodId += 1;
          return false;
        }),
    ),
  );

  if (skippedForMissingPeriodId > 0) {
    console.warn(
      `persistPeriodInfo: skipped ${skippedForMissingPeriodId} row(s) because their period has not been created on the server yet.`,
    );
  }

  await NitroService.createPeriodInfo({ periodInfoList });
};

const createPlaceholderPeriod = async (
  marketer: string,
  product: string,
  origin: IFavoriteLocationExtension,
  monthNum: number,
  year: number,
  half: MonthColumn["half"],
) => {
  const lastDay = new Date(year, monthNum, 0).getDate();
  const startDate = { year, month: monthNum, day: half === "h2" ? 16 : 1 };
  const endDate = {
    year,
    month: monthNum,
    day: half === "h1" ? 15 : lastDay,
  };

  const created = (
    await NitroService.createPeriodPrice({
      favLocId: origin.id || getFavoriteLocId(product, origin),
      month: { year, month: monthNum, day: 1 },
      split: half !== "full",
      startDate,
      endDate,
      name: "posted",
      price: null,
      showAs: "DISCUSS",
      updatedBy: marketer,
    })
  )[0];

  if (!created?.periodId) return null;

  return {
    favMonthId: created.favMonthId,
    periodId: created.periodId,
    startDate,
    endDate,
  };
};

export const persistAdditionalTypes = async (
  marketer: string,
  product: string,
  sendPanelOrigin: IFavoriteLocationExtension,
  customRows: SendPanelCustomRow[],
  monthColumns: MonthColumn[],
) => {
  const activeCustomRows = customRows.filter(
    (row) => row.priceType.trim() !== "",
  );

  const columnsNeedingPeriod = monthColumns.filter((col, colIndex) => {
    const colMonthNum = MONTHS_LOCAL.indexOf(col.month) + 1;
    const hasExistingMonth = sendPanelOrigin.months.some(
      (m) => m.parsedMonth.month === colMonthNum,
    );
    if (hasExistingMonth) return false;
    const field = `month-${colIndex}`;
    return activeCustomRows.some(
      (row) => (row.values[field] ?? "").trim() !== "",
    );
  });

  const currentMonthIndex = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const newlyCreatedMonths = (
    await Promise.all(
      columnsNeedingPeriod.map(
        async (col): Promise<IFavoriteMonthExtension | null> => {
          const monthNum = MONTHS_LOCAL.indexOf(col.month) + 1;
          const year =
            MONTHS_LOCAL.indexOf(col.month) >= currentMonthIndex
              ? currentYear
              : currentYear + 1;

          const created = await createPlaceholderPeriod(
            marketer,
            product,
            sendPanelOrigin,
            monthNum,
            year,
            col.half,
          );
          if (!created) return null;

          const firstPeriod = {
            id: created.periodId,
            startDate: `${created.startDate.year}-${created.startDate.month
              .toString()
              .padStart(2, "0")}-${created.startDate.day
              .toString()
              .padStart(2, "0")}`,
            endDate: `${created.endDate.year}-${created.endDate.month
              .toString()
              .padStart(2, "0")}-${created.endDate.day
              .toString()
              .padStart(2, "0")}`,
            posted: null,
            truck: null,
            rail: null,
            infos: [],
          };

          return {
            id: created.favMonthId,
            month: `${year}-${monthNum.toString().padStart(2, "0")}-01`,
            parsedMonth: {
              year,
              month: monthNum,
              day: 1,
            },
            periods: [firstPeriod],
            split: col.half !== "full",
            firstPeriod,
            secondPeriod: null,
          };
        },
      ),
    )
  ).filter((month): month is IFavoriteMonthExtension => month !== null);

  const allMonths = [...sendPanelOrigin.months, ...newlyCreatedMonths];
  const updatedOrigin: IFavoriteLocationExtension = {
    ...sendPanelOrigin,
    months: allMonths.map((monthEntry) => {
      const periods = monthEntry.periods.map((period) => {
        const monthNum = monthEntry.parsedMonth.month;
        const monthIsSplitInUI = monthColumns.some(
          (c) =>
            MONTHS_LOCAL.indexOf(c.month) + 1 === monthNum && c.half !== "full",
        );

        const periodDay = Number(period?.startDate?.split("-")[2]);
        const periodHalf: "h1" | "h2" = periodDay <= 15 ? "h1" : "h2";
        const matchingColumnIndex = monthColumns.findIndex((col) => {
          const colMonthNum = MONTHS_LOCAL.indexOf(col.month) + 1;
          if (colMonthNum !== monthNum) return false;
          if (monthIsSplitInUI) {
            return col.half === periodHalf;
          }
          return col.half === "full";
        });

        const infos: IFavoritePeriodInfo[] = activeCustomRows.map(
          (row, rowIndex) => {
            const existingType = (period?.infos ?? []).find(
              (additionalType) =>
                additionalType.name.trim() === row.priceType.trim(),
            );
            const rawValue =
              matchingColumnIndex === -1
                ? existingType?.value ?? ""
                : row.values[`month-${matchingColumnIndex}`] ?? "";
            const parsedRaw = parseFloat(rawValue);

            return {
              name: row.priceType,
              price: Number.isFinite(parsedRaw) ? parsedRaw : null,
              showAs: null as null,
              value: rawValue ?? "",
              order: rowIndex + 1,
              id: "",
              updatedAt: "",
              updatedBy: "",
            };
          },
        );

        if (!period) return null;
        return {
          ...period,
          infos,
        };
      });
      return {
        ...monthEntry,
        periods,
        firstPeriod: periods[0] ?? null,
        secondPeriod: monthEntry.split ? periods[1] : null,
      };
    }),
  };

  await persistPeriodInfo(marketer, updatedOrigin);
};

export const toggleSplitMonth = async (
  marketer: string,
  monthEntry: IFavoriteMonthExtension,
  shouldUnsplit: boolean,
) => {
  if (!monthEntry.id) {
    return;
  }

  const lastDay = new Date(
    monthEntry.parsedMonth.year,
    monthEntry.parsedMonth.month,
    0,
  ).getDate();
  const updatedAt = toDateTimeParts();

  if (shouldUnsplit) {
    return NitroService.unsplitMonth({
      favMonthId: monthEntry.id,
      newFirstPeriodEndDate: {
        year: monthEntry.parsedMonth.year,
        month: monthEntry.parsedMonth.month,
        day: lastDay,
      },
      updatedBy: marketer,
      updatedAt,
    });
  }

  return NitroService.splitMonth({
    favMonthId: monthEntry.id,
    newFirstPeriodEndDate: {
      year: monthEntry.parsedMonth.year,
      month: monthEntry.parsedMonth.month,
      day: 15,
    },
    updatedBy: marketer,
    updatedAt,
  });
};

export const persistSplitStateDelta = async (
  marketer: string,
  sourceOrigin: IFavoriteLocationExtension,
  editedOrigin: IFavoriteLocationExtension,
) => {
  const updatedAt = toDateTimeParts();
  await Promise.all(
    editedOrigin.months.map(async (monthEntry) => {
      const sourceMonth = sourceOrigin.months.find(
        (source) =>
          source.parsedMonth.year === monthEntry.parsedMonth.year &&
          source.parsedMonth.month === monthEntry.parsedMonth.month,
      );

      if (
        !monthEntry.id ||
        !sourceMonth ||
        sourceMonth.split === monthEntry.split
      ) {
        return;
      }

      const lastDay = new Date(
        monthEntry.parsedMonth.year,
        monthEntry.parsedMonth.month,
        0,
      ).getDate();

      if (monthEntry.split) {
        await NitroService.splitMonth({
          favMonthId: monthEntry.id,
          newFirstPeriodEndDate: {
            year: monthEntry.parsedMonth.year,
            month: monthEntry.parsedMonth.month,
            day: 15,
          },
          updatedBy: marketer,
          updatedAt,
        });
        return;
      }

      await NitroService.unsplitMonth({
        favMonthId: monthEntry.id,
        newFirstPeriodEndDate: {
          year: monthEntry.parsedMonth.year,
          month: monthEntry.parsedMonth.month,
          day: lastDay,
        },
        updatedBy: marketer,
        updatedAt,
      });
    }),
  );
};

const inFlightPeriodPriceRequests = new Map<
  string,
  Promise<ICreatePeriodPriceResponse | undefined>
>();

export const persistPeriodPrice = async (params: {
  marketer: string;
  product: string;
  editedOrigin: IFavoriteLocationExtension;
  monthEntry: IFavoriteMonthExtension;
  period: IFavoritePeriod;
  mode: "posted" | "truck" | "rail";
}) => {
  const { marketer, product, editedOrigin, monthEntry, period, mode } = params;
  const favLocId = editedOrigin.id || getFavoriteLocId(product, editedOrigin);
  // startDate/endDate are "YYYY-MM-DD" strings on the raw type, not {year,month,day} objects.
  const [startYear, startMonth, startDay] = period.startDate
    .split("-")
    .map(Number);
  const [endYear, endMonth, endDay] = period.endDate.split("-").map(Number);
  const periodStartDate = { year: startYear, month: startMonth, day: startDay };
  const periodEndDate = { year: endYear, month: endMonth, day: endDay };
  const requestKey = [
    favLocId,
    monthEntry.parsedMonth.year,
    monthEntry.parsedMonth.month,
    periodStartDate.day,
    periodEndDate.day,
    mode,
  ].join("|");

  const existingRequest = inFlightPeriodPriceRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const run = async () => {
    const periodPrice = period[mode];
    const showAs: FavoritePriceShowAs = periodPrice?.showAs || "PRICE";
    const price = periodPrice?.price ?? null;

    if (periodPrice?.id) {
      await NitroService.updatePeriodPrice({
        periodPriceId: periodPrice.id,
        price,
        showAs,
        updatedBy: marketer,
      });
      return undefined;
    }

    if (price === null && showAs === "PRICE") {
      return undefined;
    }

    const created = await NitroService.createPeriodPrice({
      favLocId,
      month: {
        year: monthEntry.parsedMonth.year,
        month: monthEntry.parsedMonth.month,
        day: 1,
      },
      split: monthEntry.split,
      startDate: periodStartDate,
      endDate: periodEndDate,
      name: mode,
      price,
      showAs,
      updatedBy: marketer,
    });

    return created[0];
  };

  const request = run().finally(() => {
    inFlightPeriodPriceRequests.delete(requestKey);
  });
  inFlightPeriodPriceRequests.set(requestKey, request);
  return request;
};

export const persistPeriodPriceMobile = async (params: {
  marketer: string;
  product: string;
  editedOrigin: IFavoriteLocationExtension;
  monthEntry: IFavoriteMonthExtension;
  period: IFavoritePeriod | null;
  mode: "posted" | "truck" | "rail";
}) => {
  const { marketer, product, editedOrigin, monthEntry, period, mode } = params;
  const favLocId = editedOrigin.id || getFavoriteLocId(product, editedOrigin);
  if (!period) {
    throw new Error("Period is null");
  }
  // startDate/endDate are "YYYY-MM-DD" strings on the raw type, not {year,month,day} objects.
  const [startYear, startMonth, startDay] = period.startDate
    .split("-")
    .map(Number);
  const [endYear, endMonth, endDay] = period.endDate.split("-").map(Number);
  const periodStartDate = { year: startYear, month: startMonth, day: startDay };
  const periodEndDate = { year: endYear, month: endMonth, day: endDay };
  const requestKey = [
    favLocId,
    monthEntry.parsedMonth.year,
    monthEntry.parsedMonth.month,
    startDay,
    endDay,
    mode,
  ].join("|");

  const existingRequest = inFlightPeriodPriceRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const run = async () => {
    const periodPrice = period[mode];
    const showAs: FavoritePriceShowAs = periodPrice?.showAs || "PRICE";
    const price = periodPrice?.price ?? null;

    if (periodPrice?.id) {
      await NitroService.updatePeriodPrice({
        periodPriceId: periodPrice.id,
        price,
        showAs,
        updatedBy: marketer,
      });
      return undefined;
    }

    if (price === null && showAs === "PRICE") {
      return undefined;
    }

    const created = await NitroService.createPeriodPrice({
      favLocId,
      month: {
        year: monthEntry.parsedMonth.year,
        month: monthEntry.parsedMonth.month,
        day: 1,
      },
      split: monthEntry.split,
      startDate: periodStartDate,
      endDate: periodEndDate,
      name: mode,
      price,
      showAs,
      updatedBy: marketer,
    });

    return created[0];
  };

  const request = run().finally(() => {
    inFlightPeriodPriceRequests.delete(requestKey);
  });
  inFlightPeriodPriceRequests.set(requestKey, request);
  return request;
};
